import { useNavigate } from "react-router"
import DashboardLayout from "../../../components/DashboardLayout"
import FleetrifyCard from "../../../components/Card"
import { fleetrifyIcons } from "../../../assets/icons/icons"
import { activityHeader, activityTimelineData, deliveryDetailsHeader, orderData, orderSummaryDeliveryDetails, orderSummaryHeaders, packageDetails, paymentInformation } from "../../../utils/mock"
import { cn } from "../../../utils/cn"
import { CheckCircle, Circle } from "lucide-react"

type Header = {
    field: string;
    value: string;
};

type OrderDetailProps = {
    headers: Header;
    data: {
        label: string;
        value: string | React.ReactNode;
        isStatus?: boolean;
    }[];
};

const OrderDetails = () => {
    const navigate = useNavigate()


    const OrderDetailsTable: React.FC<OrderDetailProps> = ({ headers, data }) => {
        return (
            <div className="overflow-x-auto">
                <table className="min-w-full border-collapse border-spacing-y-2">
                    <thead>
                        <tr className="text-left border-b">
                            <th className="py-2 px-4 font-semibold text-gray-700">
                                {headers.field}
                            </th>
                            <th className="py-2 px-4 font-semibold text-gray-700">
                                {headers.value}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, index) => (
                            <tr key={index} className="border-b  last:border-0">
                                <td className="py-3 px-4 font-medium text-gray-700">
                                    {row.label}
                                </td>
                                <td className="py-3 px-4">
                                    {row.isStatus ? (
                                        <span
                                            className={cn(
                                                "px-3 py-1 rounded-full text-sm font-medium border",
                                                row.value === "Paid" &&
                                                "border-green-400 text-green-600 bg-green-50",
                                                row.value === "In Transit" &&
                                                "border-blue-400 text-blue-600 bg-blue-50",
                                                row.value === "" &&
                                                "border-gray-400 text-gray-600 bg-gray-50",
                                            )}
                                        >
                                            {row.value}
                                        </span>
                                    ) : (
                                        row.value
                                    )}
                                </td>

                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };


    const steps = [
        { label: "Order Received", status: "completed" },
        { label: "Package Picked Up", status: "completed" },
        { label: "In Transit", status: "current" },
        { label: "Out for Delivery", status: "upcoming" },
        { label: "Delivered", status: "upcoming" },
    ];

    const timeline = [
        { time: "1 hour ago", date: "July 16, 2025 – 11:15 AM", detail: "Left Lagos sorting facility" },
        { time: "3 hours ago", date: "July 16, 2025 – 09:15 AM", detail: "Package scanned at Lagos warehouse" },
        { time: "Yesterday", date: "July 15, 2025 – 06:45 PM", detail: "Package picked up by driver" },
        { time: "Yesterday", date: "July 15, 2025 – 09:00 AM", detail: "Order confirmed and processed" },
    ];



    const DeliveryProgress = () => {
        const activeIndex = steps.findIndex(
            (s) => s.status === "current"
        );
        return (
            <div className="w-full max-w-4xl mx-auto bg-white p-6 rounded-2xl shadow">
                <h2 className="text-xl font-semibold mb-6">Delivery Progress Bar</h2>

                <div className="flex items-center justify-between overflow-x-auto custom-scrollbar">
                    {steps.map((step, idx) => (
                        <div key={idx} className="flex items-center ">
                            {/* Circle */}
                            <div className="flex flex-col items-center">
                                {step.status === "completed" ? (
                                    <CheckCircle className="w-8 h-8 text-green-500 bg-white rounded-full" />
                                ) : step.status === "current" ? (
                                    <Circle className="w-8 h-8 text-yellow-500 bg-white rounded-full" />
                                ) : (
                                    <Circle className="w-8 h-8 text-gray-300 bg-white rounded-full" />
                                )}
                                <span
                                    className={`mt-2 text-sm font-medium text-center ${step.status === "completed"
                                        ? "text-green-600"
                                        : step.status === "current"
                                            ? "text-yellow-600"
                                            : "text-gray-400"
                                        }`}
                                >
                                    {step.label}
                                </span>
                            </div>

                            {/* Connector Line */}
                            {idx < steps.length - 1 && (
                                <div
                                    className={`w-16 h-0.5 mx-2 self-center ${step.status === "completed"
                                        ? "bg-green-500"
                                        : step.status === "current"
                                            ? "bg-yellow-500"
                                            : "bg-gray-300"
                                        }`}
                                />
                            )}
                        </div>
                    ))}
                </div>

                {/* Divider */}
                <hr className="my-6" />

                {/* Timeline (Scrollable) */}
                <div className="overflow-x-auto custom-scrollbar">
                    <div className="flex gap-6 min-w-max">
                        {timeline.map((item, idx) => (
                            <div
                                key={idx}
                                className="bg-gray-50 p-4 rounded-xl shadow-sm min-w-[250px] flex-shrink-0"
                            >
                                <p className="text-sm text-gray-500">{item.time}</p>
                                <p className="text-xs text-gray-400">{item.date}</p>
                                <p className="mt-2 font-medium text-gray-700">{item.detail}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div >
        );
    };

    return (
        <DashboardLayout
            title="Order Details"
            pagePath={
                <div className='text-sm text-gray-500 '>
                    <button onClick={() => navigate(-1)} className=' cursor-pointer'>Orders</button> {">"} <span className='text-[#143887] font-bold'>Order Details</span>
                </div>
            }
        >
            <main className="space-y-6 my-10">
                {/* first section layer start */}
                <section className="grid md:grid-cols-2 grid-cols-1 w-full gap-6">
                    {/* first card */}
                    <FleetrifyCard cardContainerClass="pb-10 !rounded-2xl">
                        <div className="flex justify-between items-center" >
                            <div className="flex items-center gap-3">
                                <figure>{fleetrifyIcons.orderSummaryFile}</figure>
                                <h2 className="text-[#00032A] font-bold text-lg">Order Summary</h2>

                            </div>
                            <button type="button" className="border min-w-fit rounded-3xl  text-[#E41B1B] font-semibold border-[#E41B1B] py-2 px-4 flex gap-2 items-center">
                                <figure>
                                    {fleetrifyIcons.btnCancelIcon}
                                </figure>
                                Cancel Request
                            </button>
                        </div>
                        <div className="mt-5">
                            <OrderDetailsTable headers={orderSummaryHeaders} data={orderData} />
                        </div>
                    </FleetrifyCard>

                    {/* second card */}
                    <FleetrifyCard cardContainerClass="pb-10 !rounded-2xl">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <figure>{fleetrifyIcons.deliveryIcon}</figure>
                                <h2 className="text-[#00032A] font-bold text-lg">Delivery Route Information</h2>
                            </div>
                            <button type="button" className="border min-w-fit rounded-3xl  text-[#1A4AB2] font-semibold border-[#1A4AB2] py-2 px-4 flex gap-2 justify-between items-center">
                                <figure>
                                    {fleetrifyIcons.orderMessageIcon}
                                </figure>
                                Message driver
                            </button>
                        </div>
                        <div className="mt-5">
                            <OrderDetailsTable headers={deliveryDetailsHeader} data={orderSummaryDeliveryDetails} />
                        </div>
                    </FleetrifyCard>
                </section>
                {/* first section layer end */}

                {/* second section layer start */}
                <FleetrifyCard cardContainerClass="pb-10 !rounded-2xl">
                    <div className="flex  justify-between items-center">
                        <div className="flex items-center gap-3">
                            <figure>{fleetrifyIcons.mapIcon}</figure>
                            <h2 className="text-[#00032A] font-bold text-lg">Live Tracking & Progress</h2>
                        </div>
                        <button type="button" className="border min-w-fit rounded-3xl  text-[#1A4AB2] font-semibold border-[#1A4AB2] py-2 px-4 flex gap-2 justify-between items-center">
                            View Map
                            <figure>
                                {fleetrifyIcons.blueArrowRight}
                            </figure>
                        </button>
                    </div>

                    <div className="grid md:grid-cols-2 grid-cols-1 mt-5 justify-between gap-4 ">
                        {/* map section */}
                        <div className="border shadow w-full grid place-items-center rounded-lg min-h-40">
                            <p>Map is coming soon...</p>
                        </div>
                        <DeliveryProgress />
                    </div>
                </FleetrifyCard>
                {/* second section layer end */}

                {/* third section layer start */}
                <section className="grid md:grid-cols-2 grid-cols-1 w-full gap-6">
                    <FleetrifyCard cardContainerClass="pb-10 !rounded-2xl">
                        <div className="flex gap-3 items-center" >
                            <figure>{fleetrifyIcons.packageIcon}</figure>
                            <h2 className="text-[#00032A] font-bold text-lg">Package Information</h2>
                        </div>
                        <div className="mt-5">
                            <OrderDetailsTable headers={orderSummaryHeaders} data={packageDetails} />
                        </div>
                    </FleetrifyCard>

                    <FleetrifyCard cardContainerClass="pb-10 !rounded-2xl">
                        <div className="flex items-center gap-3">
                            <figure>{fleetrifyIcons.orderGalleryIcon}</figure>
                            <h2 className="text-[#00032A] font-bold text-lg">Proof of Pickup</h2>

                        </div>
                        <div className="mt-7">
                            <h3 className="font-bold text-base ">Photos</h3>
                            <div className="w-full mt-3 grid text-gray-400 grid-cols-2 gap-4">
                                {/* attach images here  */}
                                <div className="grid place-items-center min-h-40 rounded-2xl border bg-[#1438874D]  ">
                                    <figure>{fleetrifyIcons.galleryIcon}</figure>
                                </div>
                                <div className="grid place-items-center min-h-40 rounded-2xl border bg-[#1438874D]  ">
                                    <figure>{fleetrifyIcons.galleryIcon}</figure>
                                </div>
                                <div className="grid place-items-center min-h-40 rounded-2xl border bg-[#1438874D]  ">
                                    <figure>{fleetrifyIcons.galleryIcon}</figure>
                                </div>
                                <div className="grid place-items-center min-h-40 rounded-2xl border bg-[#1438874D]  ">
                                    <figure>{fleetrifyIcons.galleryIcon}</figure>
                                </div>
                            </div>

                        </div>
                    </FleetrifyCard>
                </section>
                {/* third section layer end */}

                {/* fourth section layer start */}
                <section className="grid md:grid-cols-2 grid-cols-1 w-full gap-6">
                    <FleetrifyCard cardContainerClass="pb-10 !rounded-2xl">
                        <div className="flex justify-between items-center" >
                            <div className="flex items-center gap-3">
                                <figure>{fleetrifyIcons.activityIcon}</figure>
                                <h2 className="text-[#00032A] font-bold text-lg">Activity Timeline</h2>

                            </div>
                            <button type="button" className="border min-w-fit rounded-3xl  text-[#1A4AB2] font-semibold border-[#1A4AB2] py-2 px-4 flex gap-2 items-center">
                                <figure>
                                    {fleetrifyIcons.orderEyeIcon}
                                </figure>
                                View full time
                            </button>
                        </div>
                        <div className="mt-5">
                            <OrderDetailsTable headers={activityHeader} data={activityTimelineData} />
                        </div>
                    </FleetrifyCard>

                    <FleetrifyCard cardContainerClass="pb-10 !rounded-2xl">
                        <div className="flex justify-between items-center" >
                            <div className="flex items-center gap-3">
                                <figure>{fleetrifyIcons.paymentIcon}</figure>
                                <h2 className="text-[#00032A] font-bold text-lg">Payment Information</h2>

                            </div>
                            <button type="button" className="border min-w-fit rounded-3xl text-[#1A4AB2] font-semibold border-[#1A4AB2] py-2 px-4 flex gap-2 items-center">
                                <figure>
                                    {fleetrifyIcons.orderEyeIcon}
                                </figure>
                                View Invoice
                            </button>
                        </div>
                        <div className="mt-5">
                            <OrderDetailsTable headers={orderSummaryHeaders} data={paymentInformation} />
                        </div>
                    </FleetrifyCard>
                </section>
                {/* fourth section layer end */}

            </main>
        </DashboardLayout >
    )
}

export default OrderDetails 